import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WhatsApp } from '@mui/icons-material';
import { 
  QuizContainer, 
  StepTitle, 
  OptionsGrid, 
  OptionCard, 
  InputWrapper, 
  Label, 
  Input, 
  ButtonGroup, 
  NavButton,
  ProgressBar,
  ProgressFill 
} from './styles';

const STEPS = [
  {
    id: 'purpose',
    title: 'מה מטרת המשכנתא?',
    options: [
      { id: 'new', label: 'רכישת דירה חדשה', icon: '🏠' },
      { id: 'refinance', label: 'מיחזור משכנתא קיימת', icon: '♻️' },
      { id: 'consolidation', label: 'איחוד הלוואות', icon: '💰' },
      { id: 'commercial', label: 'נכס מסחרי', icon: '🏢' }
    ]
  },
  {
    id: 'propertyValue',
    title: 'מה שווי הנכס המוערך?',
    type: 'number',
    label: 'שווי נכס (₪)',
    placeholder: 'לדוגמה: 2,000,000'
  },
  {
    id: 'equity',
    title: 'כמה הון עצמי עומד לרשותכם?',
    type: 'number',
    label: 'הון עצמי (₪)',
    placeholder: 'לדוגמה: 500,000'
  },
  {
    id: 'income',
    title: 'מה ההכנסה החודשית נטו (של כל הלווים)?',
    type: 'number',
    label: 'הכנסה חודשית (₪)',
    placeholder: 'לדוגמה: 15,000'
  },
  {
    id: 'name',
    title: 'רגע לפני ששולחים, איך קוראים לכם?',
    type: 'text',
    label: 'שם מלא',
    placeholder: 'הכנס שם כאן'
  }
];

const MortgageQuiz = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [isFinished, setIsFinished] = useState(false);

  const handleOptionSelect = (id, value) => {
    setFormData({ ...formData, [id]: value });
    setTimeout(() => nextStep(), 300);
  };

  const handleInputChange = (id, value) => {
    setFormData({ ...formData, [id]: value });
  };

  const sendToWhatsApp = () => {
    const purposeMap = {
      'new': 'רכישת דירה חדשה',
      'refinance': 'מיחזור משכנתא קיימת',
      'consolidation': 'איחוד הלוואות',
      'commercial': 'נכס מסחרי'
    };

    const message = `שלום אפיק, אני מעוניין בייעוץ משכנתא.
הנה הפרטים שלי מהשאלון באתר:
*שם:* ${formData.name}
*מטרה:* ${purposeMap[formData.purpose]}
*שווי נכס:* ${Number(formData.propertyValue).toLocaleString()} ₪
*הון עצמי:* ${Number(formData.equity).toLocaleString()} ₪
*הכנסה חודשית:* ${Number(formData.income).toLocaleString()} ₪`;

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/972548827511?text=${encodedMessage}`, '_blank');
    setIsFinished(true);
  };

  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      sendToWhatsApp();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  if (isFinished) {
    return (
      <QuizContainer>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <StepTitle>תודה {formData.name}!</StepTitle>
          <p>הודעת הוואטסאפ נשלחה לאפיק. נחזור אליך בהקדם!</p>
          <div style={{ marginTop: '2rem' }}>
            <NavButton onClick={() => window.location.reload()}>התחל מחדש</NavButton>
          </div>
        </motion.div>
      </QuizContainer>
    );
  }

  const step = STEPS[currentStep];

  return (
    <QuizContainer>
      <ProgressBar>
        <ProgressFill 
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
        />
      </ProgressBar>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          <StepTitle>{step.title}</StepTitle>

          {step.options ? (
            <OptionsGrid>
              {step.options.map(option => (
                <OptionCard
                  key={option.id}
                  active={formData[step.id] === option.id}
                  onClick={() => handleOptionSelect(step.id, option.id)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>{option.icon}</div>
                  {option.label}
                </OptionCard>
              ))}
            </OptionsGrid>
          ) : (
            <InputWrapper>
              <Label>{step.label}</Label>
              <Input
                type={step.type}
                placeholder={step.placeholder}
                value={formData[step.id] || ''}
                onChange={(e) => handleInputChange(step.id, e.target.value)}
                autoFocus
              />
            </InputWrapper>
          )}

          <ButtonGroup>
            <NavButton 
              secondary 
              onClick={prevStep}
              disabled={currentStep === 0}
            >
              הקודם
            </NavButton>
            {!step.options && (
              <NavButton 
                onClick={nextStep}
                disabled={!formData[step.id]}
              >
                {currentStep === STEPS.length - 1 ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <WhatsApp /> שלח לוואטסאפ
                  </span>
                ) : 'המשך'}
              </NavButton>
            )}
          </ButtonGroup>
        </motion.div>
      </AnimatePresence>
    </QuizContainer>
  );
};

export default MortgageQuiz;

